import { ApplicationConfig, APP_INITIALIZER } from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';

import { routes } from '@app/app.routes';

import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { AuthInterceptor } from '@core/interceptors/auth.interceptor';
import { CorrelationInterceptor } from '@core/interceptors/correlation.interceptor';
import { AuthService } from '@services/auth/auth.service';
import { ApiSysService } from '@services/api/api-sys.service';
import { LanguageService } from '@services/language/language.service';
import { firstValueFrom } from 'rxjs';

/**
 * Runs before the router activates any route.
 * If there is a valid token in sessionStorage (e.g. after page refresh),
 * reload the personnel profile so that AuthService.userValue is populated
 * before any component constructor or guard runs.
 * If the token is expired or the request fails, clear auth state so the
 * guard redirects to Keycloak login.
 */
function initPersonnel(authService: AuthService, apiSys: ApiSysService, langService: LanguageService): () => Promise<void> {
  return async () => {
    if (!authService.isLoggedIn()) return;
    if (authService.getUser()) return; // already populated (shouldn't happen on fresh load)
    // Skip heavy init if user is landing on the consent page — the gateway will block everything else anyway
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/consent')) return;

    try {
      // Timeout de 3 s: si el gateway no responde, el app arranca igual (sin datos de personal)
      // y el guard lo redirigirá a login cuando sea necesario. Valor reducido desde 5 s
      // ahora que el gateway cachea los datos de usuario y responde en <100 ms habitualmente.
      const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('init_timeout')), 3000)
      );
      const response = await Promise.race([firstValueFrom(apiSys.getPersonnelSelf()), timeout]);
      const personnel = (response as any)?.data ?? response;
      const personnelId = personnel?.id ?? personnel?.per_id;
      if (personnelId) {
        authService.setUserValue({ personnel, token: authService.getAccessToken()! });
        langService.setPredefinedLanguageById(personnel.lang_id);
      } else {
        authService.clear();
      }
    } catch (err: any) {
      // 401/403 = token invalid or user not provisioned → force re-auth
      // Network error = backend temporarily down → keep token so user can retry
      const status = err?.status ?? err?.error?.status;
      const msg = String(err?.error?.message ?? err?.error?.detail ?? '').toLowerCase();
      if (status === 401) {
        authService.clear();
      }
      if (status === 403 && msg.includes('consent')) {
        return;
      }
    }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withComponentInputBinding()),
    // provideClientHydration() intentionally removed: SSR is disabled in this project.
    // Enabling it causes Angular to wait indefinitely for all HTTP requests to settle
    // before considering the app "stable", resulting in 9s+ load delays.
    provideHttpClient(
      withInterceptorsFromDi()
    ),
    provideAnimationsAsync(),
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CorrelationInterceptor, multi: true },
    {
      provide: APP_INITIALIZER,
      useFactory: initPersonnel,
      deps: [AuthService, ApiSysService, LanguageService],
      multi: true,
    },
  ],
};
